import { useRobotStore } from '@/stores/useRobotStore';
import type { Robot } from '@/lib/robots-api';
import { apiFetch } from '@/lib/api';
import { isKiosk } from '@shared/lib/constants';
import { robotId } from '@shared/lib/config';

export { isKiosk };

type KioskInfo = { robot_id: string; robot_name: string; robot_type: string };

let kioskInfoPromise: Promise<KioskInfo> | null = null;

// The robot is the single source of truth for its own identity/type. On the
// kiosk this is a local fetch; in hub mode (?robot_id=) apiFetch routes the
// same tiny JSON request through the hub proxy to the robot. URDF assets are
// NEVER fetched from the robot — they're static files in this build, served
// by whichever host serves the page.
function fetchKioskInfo(): Promise<KioskInfo> {
  if (!kioskInfoPromise) {
    kioskInfoPromise = apiFetch('/kiosk/info').then(
      (res) => res.json() as Promise<KioskInfo>,
    );
  }
  return kioskInfoPromise;
}

/** This robot's robot_type (manifest key), for model-specific assets like the
 * URDF viewer. Resolvable on the kiosk and in hub mode; falls back to "yam"
 * when there is no robot to ask (cloud without ?robot_id) or the ask fails. */
export async function getKioskRobotType(): Promise<string> {
  if (!isKiosk && !robotId) return 'yam';
  try {
    return (await fetchKioskInfo()).robot_type;
  } catch (err) {
    console.error('robot_type lookup failed, rendering default model', err);
    return 'yam';
  }
}

export async function bootstrapKioskRobot(): Promise<void> {
  const info = await fetchKioskInfo();
  const robot: Robot = { id: info.robot_id, name: info.robot_name };
  useRobotStore.getState().setFleet([robot]);
  useRobotStore.getState().setFleetLoading(false);
}
